<template>
  <v-card class="cx">
    <div class="cx__head">
      <div class="cx__heading">
        <span class="cx__title">Stornierungen</span>
        <span class="cx__hint">Selbststorno über die Statusseite im gewählten Zeitraum · Kulanz nur vor Produktion, Gebühr nach 30 min</span>
      </div>
    </div>
    <!-- Heutige Auslastung der Deckel je Shop (settings/cancellation + cancellationStats/{shop}_{heute}) — unabhängig vom Zeitraum -->
    <div v-if="caps" class="cx__caps">
      <div class="cx__capsHead"><span>Heute · Deckel je Shop</span><span class="cx__muted">{{ caps.enabled ? (caps.shops.length ? 'aktiv für ' + caps.shops.join(', ') : 'aktiv für alle Shops') : 'Selbststorno ist AUS' }} · Deckel {{ caps.count }} Stornos / {{ fmtEuro(caps.amount) }} · je E-Mail {{ caps.perEmail }} in 30 Tagen</span></div>
      <div class="cx__capGrid">
        <div v-for="c in capRows" :key="c.shop" class="cap" :class="{ 'cap--warn': c.pct >= 70, 'cap--full': c.pct >= 100 }">
          <div class="cap__head"><span class="cap__shop">{{ c.shop }}</span><span class="cap__num">{{ c.count }}/{{ caps.count }} · {{ fmtEuro(c.amount) }}</span></div>
          <div class="cap__track"><div class="cap__fill" :style="{ width: Math.min(100, c.pct) + '%' }" /></div>
          <div class="cap__sub">{{ c.pct >= 100 ? 'Deckel erreicht — Knopf auf der Statusseite ausgeblendet, Anfragen laufen als Ticket' : c.pct >= 70 ? 'fast erreicht' : 'ok' }}</div>
        </div>
      </div>
    </div>
    <div v-if="loading" class="cx__empty"><v-progress-circular indeterminate size="18" width="2" class="mr-2" />Wird geladen …</div>
    <div v-else-if="error" class="cx__empty">{{ error }}</div>
    <div v-else-if="!events.length" class="cx__empty">Keine Storno-Vorgänge im Zeitraum.</div>
    <template v-else>
      <div class="cx__kpis">
        <div class="kpi"><div class="kpi__label">Gestartet</div><div class="kpi__value">{{ started }}</div><div class="kpi__sub">Storno-Dialog bis zur Entscheidung</div></div>
        <div class="kpi"><div class="kpi__label">Storniert</div><div class="kpi__value">{{ done.length }}</div><div class="kpi__sub">{{ doneFull }} komplett · {{ done.length - doneFull }} teilweise</div></div>
        <div class="kpi"><div class="kpi__label">Alternative gewählt</div><div class="kpi__value kpi__value--good">{{ alternatives.length }}</div><div class="kpi__sub">statt Storno · {{ retentionRate }}</div></div>
        <div class="kpi"><div class="kpi__label">Erstattet</div><div class="kpi__value">{{ fmtEuro(refundSum) }}</div><div class="kpi__sub">{{ refundCount }}× Erstattung · Gebühren {{ fmtEuro(feeSum) }}</div></div>
        <div class="kpi"><div class="kpi__label">Gutscheine</div><div class="kpi__value">{{ fmtEuro(voucherSum) }}</div><div class="kpi__sub">{{ voucherCount }}× Gutschein in voller Höhe</div></div>
        <div class="kpi"><div class="kpi__label">Manuell</div><div class="kpi__value" :class="{ 'kpi__value--warn': tickets.length }">{{ tickets.length }}</div><div class="kpi__sub">Ticket statt Automatik{{ failed.length ? ` · ${failed.length} fehlgeschlagen` : '' }}</div></div>
      </div>
      <div class="cx__cols">
        <div class="cx__col">
          <div class="cx__colTitle">Gründe</div>
          <div v-for="r in reasonRows" :key="r.key" class="bar">
            <span class="bar__label">{{ r.label }}</span>
            <span class="bar__track"><span class="bar__fill" :style="{ width: r.pct + '%' }" /></span>
            <span class="bar__num">{{ r.n }}</span>
          </div>
        </div>
        <div class="cx__col">
          <div class="cx__colTitle">Alternativen statt Storno</div>
          <div v-for="a in altRows" :key="a.key" class="bar">
            <span class="bar__label">{{ a.label }}</span>
            <span class="bar__track"><span class="bar__fill bar__fill--good" :style="{ width: a.pct + '%' }" /></span>
            <span class="bar__num">{{ a.n }}</span>
          </div>
          <div v-if="!altRows.length" class="cx__muted">Noch keine.</div>
        </div>
        <div class="cx__col">
          <div class="cx__colTitle">Manuelle Fälle</div>
          <div v-for="t in tickets.slice(0, 6)" :key="t.id" class="cx__ticket">
            <span class="cx__ticketName">{{ t.orderName }}</span>
            <span class="cx__muted">{{ t.manualReason || t.error || '–' }}</span>
          </div>
          <div v-if="!tickets.length" class="cx__muted">Keine.</div>
        </div>
      </div>
      <div class="cx__list">
        <div class="cx__listHead"><span>Letzte Stornos</span><span class="cx__muted">{{ done.length }} im Zeitraum</span></div>
        <div v-for="d in done.slice(0, 8)" :key="d.id" class="cx__row">
          <span class="cx__rowDate">{{ fmtDate(d.at) }}</span>
          <span class="cx__rowName">{{ d.orderName }}</span>
          <span class="cx__rowTag">{{ d.full ? 'komplett' : 'teilweise' }}</span>
          <span class="cx__rowTag">{{ d.choice === 'voucher' ? 'Gutschein' : 'Erstattung' }}</span>
          <span class="cx__rowTag">{{ reasonLabel(d.reason) }}</span>
          <span class="cx__rowNum">{{ fmtMoney(d.amount, d.currency) }}<span v-if="d.fee" class="cx__muted"> · Gebühr {{ fmtMoney(d.fee, d.currency) }}</span></span>
        </div>
      </div>
    </template>
  </v-card>
</template>

<script>
// Storno-Kachel (02.11.2026): liest cancellationEvents (rateTaskApp/cancellation.js schreibt je Schritt einen Eintrag:
// alternative | requested | ticket | done | failed) im Zeitraum der Statistik, gefiltert auf die gewählten Shops.
// Beträge in Shop-Währung; die Summen hier addieren EUR-Shops direkt und zeigen andere Währungen mit Kürzel.
import { collection, doc, getDoc, getDocs, limit, orderBy, query, where } from "firebase/firestore";
import { db } from "../../../fb";
import { mapGetters } from "vuex";
import { getDecimalNumberString } from "@/utils";

const REASONS = { address: "Adresse falsch", dislike: "Gefällt nicht", size: "Falsche Größe", other: "Sonstiges" };
const ALTS = { address: "Adresse geändert", edit: "Bestellung bearbeitet", changeRequest: "Änderungswunsch", size: "Größe geändert" };

export default {
  name: "CancellationsCard",
  props: {
    shopCodes: { type: Array, default: () => [] },
  },
  data() {
    return { events: [], loading: false, error: "", caps: null, capStats: {} };
  },
  computed: {
    ...mapGetters(["statisticRange"]),
    filtered() {
      if (!this.shopCodes.length) return this.events;
      return this.events.filter((e) => this.shopCodes.includes(e.shopKey));
    },
    done() { return this.filtered.filter((e) => e.type === "done"); },
    tickets() { return this.filtered.filter((e) => e.type === "ticket"); },
    failed() { return this.filtered.filter((e) => e.type === "failed"); },
    alternatives() { return this.filtered.filter((e) => e.type === "alternative"); },
    requested() { return this.filtered.filter((e) => e.type === "requested"); },
    // Gestartet = Vorgänge, die bis zur Entscheidung kamen: Alternative gewählt ODER Code angefordert ODER Ticket
    started() {
      const orders = new Set([...this.alternatives, ...this.requested, ...this.tickets].map((e) => e.orderUid));
      return orders.size;
    },
    doneFull() { return this.done.filter((e) => e.full).length; },
    refundCount() { return this.done.filter((e) => e.choice === "refund").length; },
    voucherCount() { return this.done.filter((e) => e.choice === "voucher").length; },
    refundSum() { return this.done.filter((e) => e.choice === "refund").reduce((s, e) => s + (Number(e.amount) || 0), 0); },
    voucherSum() { return this.done.filter((e) => e.choice === "voucher").reduce((s, e) => s + (Number(e.amount) || 0), 0); },
    feeSum() { return this.done.filter((e) => e.choice === "refund").reduce((s, e) => s + (Number(e.fee) || 0), 0); },
    /** Auslastung heute je Shop: alle Shops der Statistik (Filter beachten), auch ohne Einträge */
    capRows() {
      if (!this.caps) return [];
      const shops = this.shopCodes.length ? this.shopCodes : Object.keys(this.capStats);
      return shops.map((shop) => {
        const s = this.capStats[shop] || {};
        const count = Number(s.count) || 0;
        const amount = Number(s.amount) || 0;
        const pct = Math.max(this.caps.count ? (count / this.caps.count) * 100 : 0, this.caps.amount ? (amount / this.caps.amount) * 100 : 0);
        return { shop, count, amount, pct: Math.round(pct) };
      });
    },
    retentionRate() {
      const total = this.started;
      if (!total) return "–";
      return `${Math.round((this.alternatives.length / total) * 100)} % der Starts`;
    },
    reasonRows() {
      const counts = {};
      for (const e of [...this.done, ...this.tickets, ...this.alternatives, ...this.requested]) counts[e.reason] = (counts[e.reason] || 0) + 1;
      const max = Math.max(1, ...Object.values(counts));
      return Object.entries(counts).sort((a, b) => b[1] - a[1]).map(([key, n]) => ({ key, label: REASONS[key] || key, n, pct: Math.round((n / max) * 100) }));
    },
    altRows() {
      const counts = {};
      for (const e of this.alternatives) counts[e.alt] = (counts[e.alt] || 0) + 1;
      const max = Math.max(1, ...Object.values(counts));
      return Object.entries(counts).sort((a, b) => b[1] - a[1]).map(([key, n]) => ({ key, label: ALTS[key] || key, n, pct: Math.round((n / max) * 100) }));
    },
  },
  watch: {
    statisticRange: { immediate: true, handler() { this.load(); } },
    shopCodes: { immediate: true, handler() { this.loadCaps(); } },
  },
  methods: {
    reasonLabel(r) { return REASONS[r] || r || "–"; },
    fmtEuro(v) { return `${getDecimalNumberString(v || 0, 2)} €`; },
    fmtMoney(v, cur) {
      try { return new Intl.NumberFormat("de-DE", { style: "currency", currency: cur || "EUR" }).format(Number(v) || 0); } catch (e) { return `${getDecimalNumberString(v || 0, 2)} ${cur || ""}`; }
    },
    fmtDate(ms) { return new Date(Number(ms)).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); },
    /** Deckel + heutige Zähler (Berliner Tag) — Settings dürfen fehlen (Rolle ohne SETTINGS) */
    async loadCaps() {
      try {
        const sSnap = await getDoc(doc(db, "settings", "cancellation")).catch(() => null);
        const s = sSnap && sSnap.exists() ? sSnap.data() : {};
        const cfg = { enabled: s.enabled === true, shops: Array.isArray(s.shops) ? s.shops : [], count: (s.dailyCap && s.dailyCap.count) || 20, amount: (s.dailyCap && s.dailyCap.amount) || 1500, perEmail: s.perEmail30d || 2 };
        const day = new Intl.DateTimeFormat("sv-SE", { timeZone: "Europe/Berlin", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
        const shops = this.shopCodes.length ? this.shopCodes : ["AQ_DE", "AQ_ES", "AQ_FR", "AQ_PL", "OH_US", "TM_DE"];
        const stats = {};
        await Promise.all(shops.map(async (shop) => {
          const snap = await getDoc(doc(db, "cancellationStats", `${shop}_${day}`)).catch(() => null);
          stats[shop] = snap && snap.exists() ? snap.data() : {};
        }));
        this.capStats = stats;
        this.caps = cfg;
      } catch (e) {
        this.caps = null;
      }
    },
    async load() {
      const r = this.statisticRange || {};
      const min = Date.parse(r.min || "") || 0;
      const max = Date.parse(r.max || "") || Date.now();
      if (!min) return;
      this.loading = true;
      this.error = "";
      try {
        const snap = await getDocs(query(collection(db, "cancellationEvents"), where("at", ">=", min), where("at", "<=", max), orderBy("at", "desc"), limit(2000)));
        this.events = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      } catch (e) {
        this.error = `Storno-Daten nicht lesbar: ${e.message}`;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.cx {
  padding: 18px 20px 16px;
  border-radius: 12px !important;
  &__head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
  &__heading { display: flex; align-items: baseline; gap: 12px; flex-wrap: wrap; }
  &__title { font-weight: 700; font-size: 15px; }
  &__hint { font-size: 12px; color: rgba(0, 0, 0, 0.5); }
  &__empty { padding: 18px 0 6px; font-size: 13px; color: rgba(0, 0, 0, 0.55); display: flex; align-items: center; }
  &__caps { margin-top: 12px; padding: 10px 12px; border-radius: 10px; background: rgba(0, 0, 0, 0.03); }
  &__capsHead { display: flex; justify-content: space-between; gap: 10px; flex-wrap: wrap; font-size: 12px; font-weight: 700; margin-bottom: 8px; }
  &__capGrid { display: grid; grid-template-columns: repeat(auto-fit, minmax(170px, 1fr)); gap: 8px; }
  &__kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 10px; margin: 14px 0; }
  &__cols { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 16px; margin-top: 6px; }
  &__colTitle { font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: rgba(0, 0, 0, 0.55); margin-bottom: 8px; }
  &__muted { font-size: 12px; color: rgba(0, 0, 0, 0.5); }
  &__ticket { display: flex; flex-direction: column; padding: 4px 0; border-bottom: 1px solid rgba(0, 0, 0, 0.05); }
  &__ticketName { font-weight: 600; font-size: 13px; }
  &__list { margin-top: 16px; border-top: 1px solid rgba(0, 0, 0, 0.06); padding-top: 10px; }
  &__listHead { display: flex; justify-content: space-between; font-size: 12px; font-weight: 700; margin-bottom: 6px; }
  &__row { display: grid; grid-template-columns: 90px 1fr auto auto auto auto; gap: 10px; align-items: center; padding: 5px 0; border-bottom: 1px solid rgba(0, 0, 0, 0.04); font-size: 13px; }
  &__rowDate { color: rgba(0, 0, 0, 0.5); font-variant-numeric: tabular-nums; }
  &__rowName { font-weight: 600; }
  &__rowTag { font-size: 11px; padding: 2px 8px; border-radius: 999px; background: rgba(0, 0, 0, 0.05); }
  &__rowNum { font-variant-numeric: tabular-nums; font-weight: 600; text-align: right; }
}
.kpi { padding: 10px 12px; border-radius: 10px; background: rgba(0, 0, 0, 0.035);
  &__label { font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; color: rgba(0, 0, 0, 0.5); }
  &__value { font-size: 20px; font-weight: 700; margin-top: 2px; font-variant-numeric: tabular-nums; &--good { color: #1b7f4d; } &--warn { color: #b26a00; } }
  &__sub { font-size: 11px; color: rgba(0, 0, 0, 0.5); margin-top: 2px; }
}
.bar { display: grid; grid-template-columns: 130px 1fr 32px; align-items: center; gap: 8px; font-size: 12px; padding: 3px 0;
  &__label { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  &__track { height: 8px; border-radius: 999px; background: rgba(0, 0, 0, 0.06); overflow: hidden; }
  &__fill { display: block; height: 100%; background: #c23434; border-radius: 999px; &--good { background: #1b7f4d; } }
  &__num { text-align: right; font-variant-numeric: tabular-nums; font-weight: 600; }
}
.cap { padding: 8px 10px; border-radius: 8px; background: #fff; border: 1px solid rgba(0, 0, 0, 0.06);
  &__head { display: flex; justify-content: space-between; font-size: 12px; gap: 6px; }
  &__shop { font-weight: 700; }
  &__num { font-variant-numeric: tabular-nums; color: rgba(0, 0, 0, 0.6); }
  &__track { height: 6px; border-radius: 999px; background: rgba(0, 0, 0, 0.07); margin: 6px 0 4px; overflow: hidden; }
  &__fill { height: 100%; background: #1b7f4d; border-radius: 999px; }
  &__sub { font-size: 11px; color: rgba(0, 0, 0, 0.5); }
  &--warn { border-color: rgba(178, 106, 0, 0.5); .cap__fill { background: #b26a00; } .cap__sub { color: #b26a00; } }
  &--full { border-color: rgba(194, 52, 52, 0.6); .cap__fill { background: #c23434; } .cap__sub { color: #c23434; font-weight: 600; } }
}
@media (max-width: 720px) {
  .cx__row { grid-template-columns: 1fr auto; }
  .cx__rowDate, .cx__rowTag { display: none; }
}
</style>
